import React, { Suspense, lazy, useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import Button from "@mui/material/Button";

import LoggedInLayout from "../layout";
import { AuthProvider, useAuth } from "../context/Auth/AuthContext";
import { WhatsAppsProvider } from "../context/WhatsApp/WhatsAppsContext";
import { ThemeProvider } from "../context/DarkMode";
import { SocketProvider, useSocket } from "../context/Socket/SocketContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import { getFrontendVersion } from "../config";

const Dashboard = lazy(() => import("../pages/Dashboard/"));
const Tickets = lazy(() => import("../pages/Tickets/"));
const Login = lazy(() => import("../pages/Login/"));
const Connections = lazy(() => import("../pages/Connections/"));
const Settings = lazy(() => import("../pages/Settings/"));
const Users = lazy(() => import("../pages/Users"));
const Contacts = lazy(() => import("../pages/Contacts/"));
const QuickAnswers = lazy(() => import("../pages/QuickAnswers/"));
const Queues = lazy(() => import("../pages/Queues/"));
const ExternalShop = lazy(() => import("../pages/ExternalShop/"));
const SystemChecks = lazy(() => import("../pages/SystemChecks/"));

const PageLoader = () => (
  <BackdropLoading />
);

const UPDATE_TOAST_ID = "app-update-available";
const RELEASE_ID_STORAGE_KEY = "appKnownFrontendReleaseId";
const LAST_PROMPTED_RELEASE_KEY = "appLastPromptedFrontendReleaseId";

const clearLocalAppDataAndReload = async () => {
  if ("serviceWorker" in navigator) {
    try {
      const registrations = await navigator.serviceWorker.getRegistrations();
      await Promise.all(registrations.map((registration) => registration.unregister()));
    } catch (_err) {
      // ignore
    }
  }

  if ("caches" in window) {
    try {
      const keys = await caches.keys();
      await Promise.all(keys.map((key) => caches.delete(key)));
    } catch (_err) {
      // ignore
    }
  }

  try {
    localStorage.clear();
  } catch (_err) {
    // ignore
  }

  try {
    sessionStorage.clear();
  } catch (_err) {
    // ignore
  }

  const reloadUrl = new URL(window.location.href);
  reloadUrl.searchParams.set("v", Date.now().toString());
  window.location.replace(reloadUrl.toString());
};

const AppUpdateNotifier = () => {
  const { isAuth } = useAuth();
  const { socket } = useSocket();
  const currentReleaseIdRef = useRef(null);

  useEffect(() => {
    if (!isAuth) {
      currentReleaseIdRef.current = null;
      toast.dismiss(UPDATE_TOAST_ID);
      return undefined;
    }

    const normalizeReleaseId = (value) =>
      typeof value === "string" ? value.trim() : "";

    const persistKnownReleaseId = (releaseId) => {
      try {
        localStorage.setItem(RELEASE_ID_STORAGE_KEY, releaseId);
      } catch (_err) {
        // ignore
      }
    };

    const readKnownReleaseId = () => {
      try {
        return normalizeReleaseId(localStorage.getItem(RELEASE_ID_STORAGE_KEY));
      } catch (_err) {
        return "";
      }
    };

    const persistPromptedReleaseId = (releaseId) => {
      try {
        sessionStorage.setItem(LAST_PROMPTED_RELEASE_KEY, releaseId);
      } catch (_err) {
        // ignore
      }
    };

    const readPromptedReleaseId = () => {
      try {
        return normalizeReleaseId(sessionStorage.getItem(LAST_PROMPTED_RELEASE_KEY));
      } catch (_err) {
        return "";
      }
    };

    const localFrontendReleaseId = normalizeReleaseId(getFrontendVersion());
    const storedReleaseId = readKnownReleaseId();

    currentReleaseIdRef.current =
      storedReleaseId || localFrontendReleaseId || currentReleaseIdRef.current;

    const showUpdateToast = (releaseId) => {
      const lastPromptedReleaseId = readPromptedReleaseId();
      if (lastPromptedReleaseId === releaseId) {
        return;
      }

      persistPromptedReleaseId(releaseId);

      toast.info(
        ({ closeToast }) => (
          <div>
            <div>{i18n.t("appUpdate.message")}</div>
            <div style={{ fontSize: "0.85rem", marginTop: 6, marginBottom: 8 }}>
              {i18n.t("appUpdate.warning")}
            </div>
            <Button
              size="small"
              variant="contained"
              color="primary"
              onClick={() => {
                closeToast();
                clearLocalAppDataAndReload();
              }}
            >
              {i18n.t("appUpdate.action")}
            </Button>
          </div>
        ),
        {
          toastId: UPDATE_TOAST_ID,
          autoClose: false,
          closeOnClick: false
        }
      );
    };

    const handleIncomingRelease = (releaseIdRaw) => {
      const releaseId = normalizeReleaseId(releaseIdRaw);

      if (!releaseId || releaseId === "unknown") {
        return;
      }

      if (!currentReleaseIdRef.current) {
        currentReleaseIdRef.current = releaseId;
        persistKnownReleaseId(releaseId);
        return;
      }

      if (currentReleaseIdRef.current !== releaseId) {
        currentReleaseIdRef.current = releaseId;
        persistKnownReleaseId(releaseId);
        showUpdateToast(releaseId);
        return;
      }

      persistKnownReleaseId(releaseId);
    };

    if (!socket) {
      return undefined;
    }

    const handleAppVersion = (payload) => {
      handleIncomingRelease(payload?.frontendReleaseId);
    };

    const handleAppUpdateAvailable = (payload) => {
      handleIncomingRelease(payload?.frontendReleaseId);
    };

    socket.on("appVersion", handleAppVersion);
    socket.on("appUpdateAvailable", handleAppUpdateAvailable);

    return () => {
      socket.off("appVersion", handleAppVersion);
      socket.off("appUpdateAvailable", handleAppUpdateAvailable);
    };
  }, [isAuth, socket]);

  return null;
};

const PrivateRoutes = () => {
  const { isAuth, loading } = useAuth();

  if (loading) {
    return <PageLoader />;
  }

  if (!isAuth) {
    return <Navigate to="/login" replace />;
  }

  return (
    <WhatsAppsProvider>
      <LoggedInLayout>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/" element={<Dashboard />} />
            <Route path="/tickets" element={<Tickets />} />
            <Route path="/tickets/:ticketId" element={<Tickets />} />
            <Route path="/connections" element={<Connections />} />
            <Route path="/contacts" element={<Contacts />} />
            <Route path="/users" element={<Users />} />
            <Route path="/quickAnswers" element={<QuickAnswers />} />
            <Route path="/Settings" element={<Settings />} />
            <Route path="/Queues" element={<Queues />} />
            <Route path="/external-shop" element={<ExternalShop />} />
            <Route path="/system-checks" element={<SystemChecks />} />
          </Routes>
        </Suspense>
      </LoggedInLayout>
    </WhatsAppsProvider>
  );
};

const AppRoutes = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <SocketProvider>
          <ThemeProvider>
            <AppUpdateNotifier />
            <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/login" element={<Login />} />
                <Route path="/*" element={<PrivateRoutes />} />
              </Routes>
            </Suspense>
            <ToastContainer autoClose={3000} />
          </ThemeProvider>
        </SocketProvider>
      </AuthProvider>
    </BrowserRouter>
  );
};

export default AppRoutes;
